import type { SkillAccessibility, SkillDraft } from "@/shared/domain/skills";

// ---------------------------------------------------------------------------
// Input type
//
// `createClientTool` casts raw LLM JSON to this shape with `params as T`, so
// nothing here is statically verified at runtime — `coerceToSkillDraft` is the
// only normalization layer between this input and the draft handed to the
// editor. It lives in its own module so both the tool's `execute` and the
// approval preview can normalize identically, without the tool ⇄ confirmation
// import cycle a shared definition in `createSkill.ts` would create.
// ---------------------------------------------------------------------------

export interface CreateSkillInput {
  name?: string;
  description?: string;
  body?: string;
  accessibility?: string;
  tags?: unknown[];
  remove_tags?: unknown[];
  /** Name of an existing skill to update instead of create. See
   *  {@link coerceToSkillDraft}. */
  target_skill_name?: string;
}

const ACCESSIBILITIES: readonly SkillAccessibility[] = [
  "private",
  "org",
  "org-editable",
];

/**
 * Strip a registry disambiguation suffix — the `(private)` / `(org)` the
 * `LoadSkill` registry appends when two visible skills share a name — from a
 * model-supplied target name, returning the bare skill name.
 *
 * The registry emits the suffix with no separator (`qa-review(private)`), but
 * the pattern tolerates surrounding whitespace since the value arrives via the
 * model. A legitimate skill name matches `SKILL_NAME_PATTERN` (letters,
 * digits, `-`, `_`; no spaces or parens), so a trailing parenthesized group is
 * always the suffix and never part of the real name. Idempotent and safe on
 * names that carry no suffix.
 */
function stripDisambiguationSuffix(name: string): string {
  return name.replace(/\s*\([^)]*\)\s*$/, "").trim();
}

function coerceTags(raw: unknown[] | undefined): string[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const value of raw) {
    if (typeof value !== "string") {
      continue;
    }
    const trimmed = value.trim();
    if (trimmed === "") {
      continue;
    }
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    tags.push(trimmed);
  }
  return tags;
}

/**
 * Normalize LLM-emitted input into a {@link SkillDraft} to seed the create
 * editor, or return `null` when the proposal carries nothing to act on — no
 * name, description, or body on a create; none of those *and* no tag changes
 * on an update.
 *
 * This is lenient by design: unlike the old "persist on approve" path, the
 * editor — not this function — is the validation seam now. A name with spaces,
 * an over-length description, or a missing field is still seeded so the user
 * lands in the editor with the model's draft and the editor's own validation
 * prompts them to fix it. Only structural noise is normalized away here:
 * outer whitespace is trimmed, blank scalars are omitted, tags are trimmed /
 * deduped (case-insensitively) / stripped of non-strings, and an unrecognized
 * accessibility is dropped so the editor keeps its `private` default rather
 * than rendering an out-of-range Select value.
 *
 * When `target_skill_name` is supplied the draft is an **update** handoff: the
 * bare name (any `(private)` / `(org)` disambiguation suffix stripped) is
 * carried as `targetSkillName` so the Skills page resolves it to an existing
 * editable skill and opens the editor in edit mode. `name` is *not* a rename
 * on that path — a skill's name is edited from the Skills list, not the
 * version editor — so it is carried only for the preview to report as
 * ignored. `tags` and `remove_tags` *are* actionable on an update (the editor
 * stages them and applies them on Save), so a target plus either one is a
 * complete request even with no content; a target on its own still returns
 * `null`.
 *
 * Both the tool's `execute` and the approval preview call this so the card the
 * user approves and the form they land in show the same draft.
 */
export function coerceToSkillDraft(input: CreateSkillInput): SkillDraft | null {
  const draft: SkillDraft = {};

  const rawTarget =
    typeof input.target_skill_name === "string"
      ? stripDisambiguationSuffix(input.target_skill_name)
      : "";
  if (rawTarget !== "") {
    draft.targetSkillName = rawTarget;
  }

  const name = typeof input.name === "string" ? input.name.trim() : "";
  if (name !== "") {
    draft.name = name;
  }
  const description =
    typeof input.description === "string" ? input.description.trim() : "";
  if (description !== "") {
    draft.description = description;
  }
  // Bodies are Markdown, so trimming outer whitespace is safe — interior
  // structure (blank lines between blocks, list markers, fenced code) carries
  // the meaning and survives.
  const body = typeof input.body === "string" ? input.body.trim() : "";
  if (body !== "") {
    draft.body = body;
  }
  // Only a recognized tier is seeded. An empty / omitted / mis-typed value
  // leaves `accessibility` unset so the editor stays on its `private` default;
  // unlike the old persist path we don't error on a typo, since the user picks
  // the tier in the editor before saving.
  if (
    typeof input.accessibility === "string" &&
    ACCESSIBILITIES.includes(input.accessibility as SkillAccessibility)
  ) {
    draft.accessibility = input.accessibility as SkillAccessibility;
  }
  const tags = coerceTags(input.tags);
  if (tags.length > 0) {
    draft.tags = tags;
  }
  // Removals are update-only: a skill being created has no tags to take off,
  // so carrying them would seed the create editor with an instruction it can't
  // honor. A tag named in both lists keeps its addition and loses its removal
  // — a contradictory proposal resolves the non-destructive way rather than
  // deleting something the same request asked to put there. Matched
  // case-insensitively, since that is how `coerceTags` dedupes.
  if (draft.targetSkillName !== undefined) {
    const added = new Set(tags.map((tag) => tag.toLowerCase()));
    const removeTags = coerceTags(input.remove_tags).filter(
      (tag) => !added.has(tag.toLowerCase()),
    );
    if (removeTags.length > 0) {
      draft.removeTags = removeTags;
    }
  }

  // Nothing worth opening the editor for — let `execute` surface an error so
  // the model re-proposes with content rather than navigating to a blank form.
  //
  // What counts as "something" depends on the mode. A create needs content:
  // tags alone describe no skill to make. An update is measured against an
  // existing skill instead, so a retag with no new content is a complete
  // request — "add the tag X to my skill" proposes nothing else, and holding
  // it to the create bar refused the only thing the user asked for.
  const hasContent =
    draft.name !== undefined ||
    draft.description !== undefined ||
    draft.body !== undefined;
  const retags = draft.tags !== undefined || draft.removeTags !== undefined;
  const isUpdate = draft.targetSkillName !== undefined;
  if (!hasContent && !(isUpdate && retags)) {
    return null;
  }
  return draft;
}
